import * as React from 'react';
import { LuX } from 'react-icons/lu';
import { cn } from '../../../helpers/utils';
import { AnimatePresence, motion } from 'motion/react';

interface DialogContext {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const DialogContext = React.createContext<DialogContext | null>(null);

const useDialogContext = () => {
  const dialogContextData = React.useContext(DialogContext);
  if (!dialogContextData) {
    throw new Error('useDialogContext must be called inside DialogProvider');
  }
  return dialogContextData;
};

const useDialog = () => {
  const [open, setOpen] = React.useState(false);
  return { open, setOpen };
};

interface DialogProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  children: React.ReactNode;
}
const Dialog = ({ children, open, setOpen }: DialogProps) => {
  return <DialogContext.Provider value={{ open, setOpen }}>{children}</DialogContext.Provider>;
};

interface DialogTriggerProps extends Omit<React.ComponentProps<'button'>, 'children'> {
  render?: (setOpen: (open: boolean) => void) => React.ReactNode;
  children?: React.ReactNode;
}
const DialogTrigger = ({ children, className, render, ...rest }: DialogTriggerProps) => {
  const { setOpen } = useDialogContext();
  if (render) {
    return render(setOpen);
  }
  return (
    <button
      className={cn('bg-supportiveRed p-4 text-white', className)}
      onClick={() => setOpen(true)}
      type="button"
      {...rest}
    >
      {children}
    </button>
  );
};

interface DialogHeaderProps extends React.ComponentProps<'div'> {
  hideClose?: boolean;
}
const DialogHeader = ({ children, className, hideClose, ...rest }: DialogHeaderProps) => {
  const { setOpen } = useDialogContext();
  return (
    <div
      className={cn(
        'flex items-center justify-between p-5 border-b border-reliableBlack20 align-middle uppercase font-bold text-base lg:text-lg z-10',
        className
      )}
      {...rest}
    >
      {children}
      {hideClose ? null : (
        <button onClick={() => setOpen(false)} type="button" role="button">
          <LuX className="w-5 lg:w-7 h-5 lg:h-7" fill="#000" />
        </button>
      )}
    </div>
  );
};

let openDialogsCount = 0;

const DialogContent = ({
  wrapperClassName = '',
  className,
  children,
  alwaysStayFull,
  slideFrom = 'bottom',
}: React.ComponentProps<'div'> & {
  wrapperClassName?: string;
  alwaysStayFull?: boolean;
  slideFrom?: 'bottom' | 'top' | 'right' | 'left';
}) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const { open, setOpen } = useDialogContext();

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Check if the click was on the backdrop (not the modal content)
    if (event.target === event.currentTarget) {
      setOpen(false);
    }
  };

  // Mapping Start Animation Position Based On Props
  const getVariants = () => {
    switch (slideFrom) {
      case 'top':
        return {
          initial: { opacity: 0, translateY: '-100%' },
          animate: { opacity: 1, translateY: '0%' },
          exit: { opacity: 0, translateY: '-100%' },
        };
      case 'left':
        return {
          initial: { opacity: 0, translateX: '-100%' },
          animate: { opacity: 1, translateX: '0%' },
          exit: { opacity: 0, translateX: '-100%' },
        };
      case 'right':
        return {
          initial: { opacity: 0, translateX: '100%' },
          animate: { opacity: 1, translateX: '0%' },
          exit: { opacity: 0, translateX: '100%' },
        };
      case 'bottom':
      default:
        return {
          initial: { opacity: 0, translateY: '100%' },
          animate: { opacity: 1, translateY: '0%' },
          exit: { opacity: 0, translateY: '100%' },
        };
    }
  };

  const variants = getVariants();

  React.useEffect(() => {
    if (open) {
      openDialogsCount += 1;

      if (openDialogsCount === 1) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        document.body.style.height = '100vh';
      }
    }

    return () => {
      if (open) {
        openDialogsCount -= 1;

        if (openDialogsCount === 0) {
          document.body.style.overflow = 'unset';
          document.body.style.paddingRight = '0px';
          document.body.style.height = 'auto';
        }
      }
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            transition={{ duration: 0.2 }}
            className={cn(
              [
                'fixed w-[100vw] h-[100dvh] z-[70] inset-0 bg-black/30 flex flex-col justify-end lg:justify-center items-center',
              ],
              wrapperClassName
            )}
            onClick={handleBackdropClick}
          >
            <motion.div
              initial={variants.initial}
              animate={variants.animate}
              exit={variants.exit}
              transition={{
                duration: 0.2,
              }}
              ref={modalRef}
              className={cn(
                [
                  'flex flex-col bg-white rounded',
                  alwaysStayFull
                    ? 'w-screen'
                    : 'w-screen lg:min-w-[600px] lg:w-fit lg:h-fit lg:max-w-2xl',
                ],
                className
              )}
            >
              {children}
            </motion.div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
};

export { Dialog, DialogTrigger, DialogHeader, DialogContent, useDialog, type DialogProps };
